import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/storefront/ProductCard";
import { PageHead, SectionHeader, EmptyState, ErrorNotice } from "@/components/storefront/common";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { productShareUrl, productShareText, shareProduct } from "@/lib/share";
import { toast } from "sonner";
import { captureAnalyticsEvent } from "@/lib/analytics";
import { Heart, Minus, Plus, Star, Truck, ShieldCheck, MapPin, Loader2, PackageX, ImagePlus, X, Share2, CheckCircle2 } from "lucide-react";
import ReviewGallery from "@/components/storefront/ReviewGallery";

const REVIEW_MAX_IMAGES = 4;

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A shared link may carry `?variant=<id>` so the recipient lands on the same variant.
  const variantParam = searchParams.get("variant");
  const { addToCart, toggleWishlist, isWishlisted } = useStore();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [pincode, setPincode] = useState("");
  const [pinResult, setPinResult] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "", title: "" });
  const [reviewImages, setReviewImages] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const viewedProductRef = useRef(null);
  const viewedProductId = data?.product?.id;
  const viewedProductCategory = data?.product?.category?.name;

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/products/slug/${slug}`).then(({ data }) => {
      setData(data);
      if (data.product) {
        const variants = data.product.variants || [];
        const requested = variantParam ? variants.find((v) => v.id === variantParam) : null;
        const active = requested
          || variants.find((v) => v.active && v.stock > 0)
          || variants[0];
        setVariant(active);
        setActiveImg(0);
        document.title = `${data.product.name} — Buy Online`;
      }
    }).catch((err) => setError(apiError(err)))
      .finally(() => setLoading(false));
  }, [slug, reloadKey, variantParam]);

  // The "link copied" confirmation fades back to the Share label on its own.
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (loading || !viewedProductId || viewedProductRef.current === viewedProductId) return;
    viewedProductRef.current = viewedProductId;
    captureAnalyticsEvent("product_viewed", {
      product_id: viewedProductId,
      product_category: viewedProductCategory,
      currency: "INR",
    });
  }, [loading, viewedProductId, viewedProductCategory]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  if (error) return <div className="container-x py-8"><ErrorNotice title="We couldn't load this product" text={error} onRetry={() => setReloadKey((k) => k + 1)} /></div>;
  if (!data?.product) return <div className="container-x py-16"><EmptyState icon={PackageX} title="Product not found" text="This product may have been removed."><Link to="/shop" className="bg-slate-900 text-white px-5 py-2.5 rounded-lg font-semibold">Back to shop</Link></EmptyState></div>;

  const p = data.product;
  const images = variant?.images?.length ? variant.images : p.images;
  const wl = isWishlisted(p.id);
  const stock = variant?.stock || 0;
  const available = stock - (variant?.reserved || 0);
  const discount = variant && variant.mrp > variant.price ? Math.round(((variant.mrp - variant.price) / variant.mrp) * 100) : 0;

  const attrKeys = [...new Set(p.variants.flatMap((v) => Object.keys(v.attributes || {})))];

  const doAdd = (buyNow) => {
    if (!variant || available < 1) return;
    addToCart({ productId: p.id, variantId: variant.id, name: p.name, slug: p.slug, image: images?.[0], price: variant.price, mrp: variant.mrp, attributes: variant.attributes, quantity: qty, stock: available });
    captureAnalyticsEvent("product_added_to_cart", {
      product_id: p.id,
      product_category: p.category?.name,
      item_count: qty,
      currency: "INR",
    });
    if (buyNow) navigate("/checkout");
  };

  // Share this product: the device's native share sheet where available (so WhatsApp
  // and any other installed app can be chosen), otherwise copy the canonical link.
  // No login is required and nothing private is ever included.
  const onShare = async () => {
    if (sharing) return;
    const url = productShareUrl({ slug: p.slug, variantId: variant?.id });
    if (!url) { toast.error("This product cannot be shared right now."); return; }
    setSharing(true);
    try {
      const result = await shareProduct({ title: p.name, text: productShareText(p.name), url });
      if (result?.ok) {
        if (result.method === "copy") { setCopied(true); toast.success("Link copied"); }
        else toast.success("Shared");
      } else if (!result?.cancelled) {
        toast.error("Could not share. Please copy the link from your browser's address bar.");
      }
    } finally {
      setSharing(false);
    }
  };

  const checkPin = async () => {
    if (!/^\d{6}$/.test(pincode)) { setPinResult({ ok: false, msg: "Enter a valid 6-digit pincode" }); return; }
    try {
      const { data } = await api.get(`/shipping/serviceability?pincode=${pincode}`);
      captureAnalyticsEvent("delivery_check_completed", {
        availability: data.serviceable === true
          ? "available"
          : data.serviceable === false ? "unavailable" : "unknown",
      });
      if (data.serviceable === false) setPinResult({ ok: false, msg: data.reason || "Not serviceable" });
      else if (data.serviceable === null) setPinResult({ ok: true, msg: data.message || "Delivery available (final rates at checkout)" });
      else setPinResult({ ok: true, msg: "Delivery available to this pincode" });
    } catch (e) {
      captureAnalyticsEvent("delivery_check_completed", { availability: "unknown" });
      setPinResult({ ok: false, msg: apiError(e) });
    }
  };

  const onPickReviewImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (!user) { toast.error("Please log in to add photos"); navigate("/login"); return; }
    const room = REVIEW_MAX_IMAGES - reviewImages.length;
    if (room <= 0) { toast.error(`You can attach up to ${REVIEW_MAX_IMAGES} images`); return; }
    setUploadingImg(true);
    try {
      for (const file of files.slice(0, room)) {
        if (!["image/jpeg", "image/png"].includes(file.type)) { toast.error(`${file.name}: only JPG/PNG allowed`); continue; }
        if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name}: must be 8 MB or smaller`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/reviews/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setReviewImages((imgs) => [...imgs, { key: data.key, url: data.url }]);
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploadingImg(false); }
  };

  const removeReviewImage = async (key) => {
    try { await api.delete(`/reviews/upload-image?key=${encodeURIComponent(key)}`); }
    catch (e) { console.debug("review image cleanup failed (removed from selection):", e?.message); }
    setReviewImages((imgs) => imgs.filter((i) => i.key !== key));
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please log in to write a review"); navigate("/login"); return; }
    try {
      await api.post("/reviews", { productId: p.id, ...reviewForm, imageKeys: reviewImages.map((i) => i.key) });
      toast.success("Review submitted for approval");
      setReviewForm({ rating: 5, comment: "", title: "" });
      setReviewImages([]);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="container-x py-8">
      <PageHead crumbs={[{ label: "Home", to: "/" }, { label: "Shop", to: "/shop" }, { label: p.category?.name, to: `/category/${p.category?.slug}` }, { label: p.name }]} title="" />
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 -mt-4">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center p-6" data-testid="product-main-image">
            <img src={resolveMediaUrl(images?.[activeImg])} alt={p.name} onError={handleMediaError} className="w-full h-full object-contain" />
          </div>
          {images?.length > 1 && (
            <div className="flex gap-3 mt-4 overflow-x-auto no-scrollbar">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                  className={`h-16 w-16 shrink-0 rounded-lg border-2 overflow-hidden bg-slate-50 ${activeImg === i ? "border-amber-500" : "border-slate-200"}`}>
                  <img src={resolveMediaUrl(img)} alt="" onError={handleMediaError} className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {p.brand?.name && <Link to={`/brand/${p.brand.slug}`} className="text-sm font-semibold uppercase tracking-wider text-amber-600">{p.brand.name}</Link>}
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-display mt-1">{p.name}</h1>
          {p.reviewCount > 0 && (
            <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-600">
              <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < Math.round(p.rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
              {p.rating} · {p.reviewCount} reviews
            </div>
          )}
          <p className="text-slate-600 mt-3">{p.shortDescription}</p>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl font-bold text-slate-900 font-mono">{formatINR(variant?.price || 0)}</span>
            {variant?.mrp > variant?.price && <span className="text-lg text-slate-400 line-through font-mono">{formatINR(variant.mrp)}</span>}
            {discount > 0 && <span className="text-sm font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200">{discount}% OFF</span>}
          </div>
          <p className="text-xs text-slate-500 mt-1">Inclusive of all taxes</p>

          {/* Variants */}
          {attrKeys.map((key) => {
            const options = [...new Set(p.variants.map((v) => v.attributes?.[key]).filter(Boolean))];
            return (
              <div key={key} className="mt-5">
                <p className="text-sm font-semibold text-slate-900 mb-2">{key}: <span className="text-slate-500 font-normal">{variant?.attributes?.[key]}</span></p>
                <div className="flex flex-wrap gap-2.5">
                  {options.map((opt) => {
                    const target = p.variants.find((v) => v.attributes?.[key] === opt && (attrKeys.every((k) => k === key || v.attributes?.[k] === variant?.attributes?.[k])));
                    const selected = variant?.attributes?.[key] === opt;
                    const oos = target && (target.stock - (target.reserved || 0)) < 1;
                    return (
                      <button key={opt} disabled={!target} onClick={() => target && setVariant(target)} data-testid={`variant-${key}-${opt}`}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${selected ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-700 hover:border-slate-400"} ${oos ? "opacity-50 line-through" : ""}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Stock */}
          <div className="mt-5">
            {available > 0 ? (
              <span className="text-sm font-semibold text-emerald-700">In stock{available <= 5 && ` · only ${available} left`}</span>
            ) : (
              <span className="text-sm font-semibold text-red-600">Out of stock</span>
            )}
          </div>

          {/* Qty + actions */}
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <div className="flex items-center border border-slate-200 rounded-lg">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3" data-testid="qty-minus"><Minus className="h-4 w-4" /></button>
              <span className="w-10 text-center font-mono font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(available, q + 1))} className="p-3" data-testid="qty-plus"><Plus className="h-4 w-4" /></button>
            </div>
            <button onClick={() => toggleWishlist(p.id)} data-testid="pdp-wishlist-btn"
              className={`p-3.5 rounded-lg border ${wl ? "border-red-200 text-red-500 bg-red-50" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
              <Heart className="h-5 w-5" fill={wl ? "currentColor" : "none"} />
            </button>
            {/* Share: native share sheet where available, otherwise copy the link. */}
            <button type="button" onClick={onShare} disabled={sharing} data-testid="pdp-share-btn"
              aria-label={copied ? "Product link copied" : `Share ${p.name}`} title="Share this product"
              className="inline-flex items-center gap-2 p-3.5 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-400 disabled:opacity-60">
              {copied ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Share2 className="h-5 w-5" />}
              <span className="text-sm font-medium" aria-live="polite" data-testid="pdp-share-label">
                {copied ? "Link copied" : "Share"}
              </span>
            </button>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button disabled={available < 1} onClick={() => doAdd(false)} data-testid="add-to-cart-btn"
              className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-semibold py-3.5 rounded-lg transition">Add to Cart</button>
            <button disabled={available < 1} onClick={() => doAdd(true)} data-testid="buy-now-btn"
              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-900 font-semibold py-3.5 rounded-lg transition">Buy Now</button>
          </div>

          {/* Pincode */}
          <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200">
            <p className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-2"><MapPin className="h-4 w-4 text-amber-500" /> Check delivery</p>
            <div className="flex gap-2">
              <input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter pincode" data-testid="pincode-input"
                className="flex-1 h-11 px-3 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-sm font-mono" />
              <button onClick={checkPin} data-testid="check-pincode-btn" className="px-5 rounded-lg bg-slate-900 text-white text-sm font-semibold">Check</button>
            </div>
            {pinResult && <p className={`text-xs mt-2 ${pinResult.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="pincode-result">{pinResult.msg}</p>}
          </div>

          <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-amber-500" /> {p.warranty || "Warranty as applicable"}</span>
            <span className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-amber-500" /> Shipped across India</span>
          </div>
        </div>
      </div>

      {/* Description + specs */}
      <div className="grid lg:grid-cols-2 gap-8 mt-12">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3">Description</h3>
          <p className="text-slate-600 leading-relaxed">{p.description}</p>
        </div>
        {p.specifications?.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Specifications</h3>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              {p.specifications.map((s, i) => (
                <div key={i} className={`grid grid-cols-2 text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                  <div className="p-3 text-slate-500 font-medium">{s.key}</div>
                  <div className="p-3 text-slate-900 font-mono">{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reviews */}
      <div className="mt-12">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Customer Reviews</h3>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {data.reviews?.length ? data.reviews.map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 text-sm">{r.userName}</span>
                    {r.verifiedPurchase && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Verified</span>}
                  </div>
                  <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
                </div>
                {r.title && <p className="font-medium text-slate-800 text-sm mt-2">{r.title}</p>}
                <p className="text-slate-600 text-sm mt-1">{r.comment}</p>
                <ReviewGallery images={r.images} />
              </div>
            )) : <p className="text-sm text-slate-500">No reviews yet. Be the first to review this product.</p>}
          </div>
          <form onSubmit={submitReview} className="rounded-xl border border-slate-200 p-4 h-fit" data-testid="review-form">
            <h4 className="font-semibold text-slate-900 mb-3">Write a review</h4>
            <div className="flex gap-1 mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <button type="button" key={i} onClick={() => setReviewForm((f) => ({ ...f, rating: i + 1 }))}>
                  <Star className={`h-6 w-6 ${i < reviewForm.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            <input value={reviewForm.title} onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title (optional)"
              className="w-full h-10 px-3 mb-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400" />
            <textarea required value={reviewForm.comment} onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))} placeholder="Share your experience" rows={3}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400" data-testid="review-comment" />

            {reviewImages.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="review-image-previews">
                {reviewImages.map((img) => (
                  <div key={img.key} className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200">
                    <img src={resolveMediaUrl(img.url)} alt="" onError={handleMediaError} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeReviewImage(img.key)} data-testid={`remove-review-image-${img.key}`}
                      className="absolute top-0.5 right-0.5 h-5 w-5 grid place-items-center rounded-full bg-slate-900/70 text-white hover:bg-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {reviewImages.length < REVIEW_MAX_IMAGES && (
              <label className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer hover:text-amber-600" data-testid="add-review-image-label">
                {uploadingImg ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {uploadingImg ? "Uploading…" : "Add photos"}
                <input type="file" accept="image/jpeg,image/png" multiple hidden disabled={uploadingImg} onChange={onPickReviewImages} data-testid="review-image-input" />
              </label>
            )}
            <p className="text-[11px] text-slate-400 mt-1">JPG or PNG, up to {REVIEW_MAX_IMAGES} photos, 8 MB each.</p>

            <button className="w-full mt-2 bg-slate-900 text-white font-semibold py-2.5 rounded-lg text-sm" data-testid="submit-review-btn">Submit Review</button>
          </form>
        </div>
      </div>

      {/* Related */}
      {data.related?.length > 0 && (
        <div className="mt-14">
          <SectionHeader title="Related Products" subtitle="You might also like" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {data.related.slice(0, 4).map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}
